import { ChevronLeft, ChevronRight } from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { cn } from "@/lib/cn";
import "./match-collection-footer.css";

export type MatchCollectionSort = "date_desc" | "date_asc" | "updated_desc";

type MatchCollectionFooterProps = {
  first: number;
  last: number;
  total: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  sort: MatchCollectionSort;
  onSortChange: (sort: MatchCollectionSort) => void;
  pageSize: string;
  onPageSizeChange: (pageSize: string) => void;
  testIdPrefix: string;
  className?: string;
};

const SORT_OPTIONS = [
  { value: "date_desc", label: "Newest" },
  { value: "date_asc", label: "Oldest" },
  { value: "updated_desc", label: "Recently updated" },
] satisfies Array<{ value: MatchCollectionSort; label: string }>;
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50].map((size) => ({
  value: String(size),
  label: String(size),
}));

/** Shared match-list chrome; each caller owns filtering, fetching and page resets. */
export function MatchCollectionFooter({
  first,
  last,
  total,
  page,
  totalPages,
  onPageChange,
  sort,
  onSortChange,
  pageSize,
  onPageSizeChange,
  testIdPrefix,
  className,
}: MatchCollectionFooterProps) {
  const pageCount = Math.max(1, totalPages);
  const currentPage = Math.max(1, Math.min(page, pageCount));
  const firstPage = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
  const visiblePages = Array.from(
    { length: Math.min(5, pageCount) },
    (_, index) => firstPage + index,
  );

  return (
    <footer className={cn("match-collection-footer", className)}>
      <span data-testid={`${testIdPrefix}-pagination-summary`}>
        {total ? first : 0}–{total ? last : 0} of {total} matches
      </span>
      <div className="match-collection-footer-controls">
        <label className="match-collection-field match-collection-sort">
          <span>Sort</span>
          <div>
            <TkticSelect
              value={sort}
              onChange={(value) => {
                const option = SORT_OPTIONS.find((item) => item.value === value);
                if (option) onSortChange(option.value);
              }}
              data-testid={`${testIdPrefix}-sort`}
              size="sm"
              ariaLabel="Sort"
              options={SORT_OPTIONS}
            />
          </div>
        </label>
        <label className="match-collection-field match-collection-page-size">
          <span>Per page</span>
          <div>
            <TkticSelect
              value={pageSize}
              onChange={onPageSizeChange}
              data-testid={`${testIdPrefix}-page-size`}
              size="sm"
              ariaLabel="Per page"
              options={PAGE_SIZE_OPTIONS}
            />
          </div>
        </label>
      </div>
      <nav className="match-collection-pages" aria-label="Match pages">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          data-testid={`${testIdPrefix}-prev-page`}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        {visiblePages.map((pageNumber) => (
          <button
            key={pageNumber}
            type="button"
            onClick={() => onPageChange(pageNumber)}
            aria-current={currentPage === pageNumber ? "page" : undefined}
            data-testid={
              currentPage === pageNumber
                ? `${testIdPrefix}-page-indicator`
                : undefined
            }
            aria-label={`Page ${pageNumber}`}
          >
            {pageNumber}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPageChange(Math.min(pageCount, currentPage + 1))}
          disabled={currentPage >= pageCount}
          data-testid={`${testIdPrefix}-next-page`}
          aria-label="Next page"
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </nav>
    </footer>
  );
}
